import { Common } from './../../../../shared/domain/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FILE_UPLOAD_LOCATION_QUOTAS,
  QUALIFICATION_CODES,
} from '../../../../shared/constants/qualification-code';
import { FeasibilityData } from '../../../../shared/interfaces/survey-meta-data.interface';
import { SurveyMetaDataService } from '../../../../shared/services/survey-meta-data/survey-meta-data.service';
import {
  FeasibilityQuota,
  FeasibilityQuotas,
  FeasibilityStatus,
} from '../../../shared/interfaces/feasibility.interface';
import { CreateSurveyService } from '../../../create-survey.service';
import { Constants } from '../header.constants';
import {
  FeasibilityCriteria,
  FeasibilityTotal,
  QualificationFormatter,
  StringFormatter,
} from './feasibility.class';
import { MultiCountryDraftService } from '../../../services/multi-country-draft/multi-country-draft.service';
import { FeasibilityDataService } from '../../../services/feasibility/feasibility-data.service';
import {
  QualificationPayload,
  RangeSet,
} from '../../../../shared/interfaces/qualification.interface';
import { ZeroDashPipe } from '../../../shared/pipes/zero-dash-pipe';
import { Observable, Subscription } from 'rxjs';
import {
  CriteriaQuota,
  DifficultQuota,
} from '../../../../shared/interfaces/feasibility.interface';
import { ToasterService } from '@purespectrum1/ui/toaster-service';
import { notifyMessage } from '../../../../shared/constants/notify-message';
import { SupportService } from '../../../services/support/support-service';
import { AuthService } from '../../../../shared/services/auth/auth.service';
import { UnicornQuotas } from '../../../../shared/interfaces/unicorn.interface';
import { share } from 'rxjs/operators';
import { QuotaPayloadService } from '../../../services/quotas/quota-payload.service';
import { SurveyUnicorns } from '../../../domain/survey-unicorns';
import { SurveyDifficultQuotas } from '../../../domain/survey-difficult-quotas';

@Component({
  selector: 'ps-ui-feasibility',
  templateUrl: './feasibility.component.html',
  styleUrls: ['./feasibility.component.css', '../header-dropdown.scss'],
  providers: [ZeroDashPipe],
})
export class FeasibilityComponent implements OnInit, OnDestroy {
  private _subscriptions: Subscription = new Subscription();
  loading: boolean = false;
  totalFeasibility?: number = 0;
  _feasibilityQuota: FeasibilityQuotas[] = [];
  audience: string[] = [];
  feasibilityStatus: FeasibilityStatus = 'none';
  public hasMcPreDraftData: boolean = false;
  private _qualifications: QualificationPayload[] = [];
  private _formatters: Map<number, QualificationFormatter> = new Map<
    number,
    QualificationFormatter
  >();
  readonly Constants = Constants;
  readonly DEFAULT_INCOME_VALUES = {
    from: 0,
    to: 9999999,
    prefix: '$',
  };
  unusedQualificationsNameList: string[] = [];
  private _unicornQuotas$!: Observable<UnicornQuotas[]>;
  private unicornQuotas: UnicornQuotas[];
  private _difficultQuotas: DifficultQuota[] = [];

  constructor(
    private _surveyMetaDataService: SurveyMetaDataService,
    private _createSurveyService: CreateSurveyService,
    private _multiCountryDraftService: MultiCountryDraftService,
    private _feasibilityService: FeasibilityDataService,
    private _supportService: SupportService,
    private _zeroDashPipe: ZeroDashPipe,
    private _toaster: ToasterService,
    private _auth: AuthService,
    private _quotaPayloadService: QuotaPayloadService
  ) {}

  ngOnInit() {
    this._subscriptions.add(
      this._surveyMetaDataService
        .getFeasibilityData()
        .subscribe((feasibility) => {
          this._mapFeasibility(feasibility);
        })
    );
    this._subscriptions.add(
      this._createSurveyService.getMultiCountryData().subscribe((data) => {
        if (!data.qualifications?.length) {
          return;
        }
        this._qualifications = data.payloadQualifications;
        const incomeData = data.qualifications.find(
          (q) => q.qualification_code === QUALIFICATION_CODES.INCOME
        )?.data;
        if (incomeData?.length) {
          this.DEFAULT_INCOME_VALUES.prefix = incomeData[0].preFix;
        }
        this._formatters = this._makeQualificationFormatters();
      })
    );

    this._subscriptions.add(
      this._multiCountryDraftService
        .getMultiCountryPreDraftData()
        .subscribe((data) => {
          this.loading = true;
          this.hasMcPreDraftData = !!data.length;
          this.totalFeasibility =
            this._feasibilityService.getTotalFeasbilityBeforeDraftSave(data);
          this.loading = false;
        })
    );
    this._initializeUnicornQuotas();
  }

  get feasibilityQuota(): FeasibilityQuotas[] {
    return this._feasibilityQuota;
  }
  set feasibilityQuota(value: FeasibilityQuotas[]) {
    this._feasibilityQuota = value;
    this.setFeasibilityStatus();
  }

  private _mapFeasibility({
    defaultQuota,
    quotaSet,
    response,
    request,
    hasQuota = false,
    loading,
  }: FeasibilityData) {
    this.loading = !!loading;
    this._difficultQuotas = response?.difficult_quotas || [];
    this.setFeasibilityStatus();
    if (!quotaSet || !response || loading) {
      this._feasibilityQuota = [];
      return;
    }
    this.loading = false;
    this.feasibilityQuota = [];

    this.audience = defaultQuota!
      // don't show in audience if there's quotas for the qualification (defaultQuota)
      // ex.: the default is show age as default quota in the quota list
      // because quotaSet always comes with age qualification when no quotas are set
      // so, when we have other quota set, the age qualification won't be
      // in the quotaSet therefore age will be shown in the audience
      .filter((q) => !quotaSet.has(q.qualification_code))
      .map((q) => {
        const qualification = this._formatters.get(q.qualification_code)!;
        return q.condition_codes
          ? qualification?.formatAudience(q.condition_codes)
          : qualification?.formatRange(q.range_sets![0]);
      });

    const mappedQuotas: FeasibilityQuotas[] = [];
    const feasibilityTotal = new FeasibilityTotal(response, quotaSet);
    const feasibilityCriteria = new FeasibilityCriteria(response, hasQuota);
    const unusedQualificationsList: { name: string; code: number }[] = [];
    quotaSet.forEach((criteriaQuotas, qualificationCode: number | string) => {
      const originalQualificationCode = qualificationCode;
      qualificationCode =
        typeof qualificationCode === 'number' ? qualificationCode : 0;
      const qualificationFormatter = this._formatters.get(qualificationCode)!;
      const isNestedQuotas = this._isNested(criteriaQuotas);
      const name = !isNestedQuotas
        ? qualificationFormatter?.name
        : this._feasibilityService.getQuotaName(
            originalQualificationCode.toString()
          );

      const quota: FeasibilityQuotas = {
        qualificationCode,
        name,
        quotas: [],
        // show `Population Data is Unavailable` message if genpop settings is not available in DB
        showQualificationMessage:
          feasibilityCriteria.unusedQualifications.includes(qualificationCode),
      };
      mappedQuotas.push(quota);
      let quotaCategoryFeasibility = 0;
      criteriaQuotas.forEach((q) => {
        qualificationCode =
          typeof qualificationCode === 'number' ? qualificationCode : 0;
        const count = feasibilityTotal.count(q.buyer_quota_id);
        quotaCategoryFeasibility += count;
        let feasible = count >= q.required_count;
        const isUnusedCriteria =
          feasibilityCriteria.unusedQualifications.includes(
            qualificationCode
          ) || feasibilityCriteria.checkUnusedCriteria(qualificationCode, q);
        if (isUnusedCriteria) {
          feasible = false; // `false` to show UNKN and red highlight class
          // we should show warning only once and not everytime feasibility is loaded.
          if (
            !this.unusedQualificationsNameList.includes(name.toUpperCase()) &&
            !isNestedQuotas
          ) {
            unusedQualificationsList.push({
              name: name.toUpperCase(),
              code: qualificationCode,
            });
          }
        }
        const feasibility = `${
          isUnusedCriteria
            ? 'UNKN'
            : this._zeroDashPipe.transform(count, '1.0-2')
        }/${this._zeroDashPipe.transform(q.required_count, '1.0-2')}`;
        const criteriaLabel = this._getCriteriaNames(q);
        let quotas: FeasibilityQuota = {
          range: criteriaLabel,
          feasible,
          feasibility,
          buyer_quota_id: q.buyer_quota_id,
          feasibiltyDifference: (count - q.required_count).toString() || null,
        };
        quota.quotas.push(quotas);
        feasibilityCriteria.checkForUnusedQualificationsWithoutQuota(
          unusedQualificationsList,
          q.criteria,
          this.unusedQualificationsNameList
        );
      });
    });
    if (unusedQualificationsList.length) {
      const names = unusedQualificationsList.map(
        (qualification) => qualification.name
      );
      const codes = unusedQualificationsList.map(
        (qualification) => qualification.code
      );

      const criterias = [...new Set(names)].join(', ');
      const qualificationCodes = [...new Set(codes)].join(', ');
      this._toaster.warning(
        notifyMessage.warningMessage.FEASIBILITY.UNUSED_CRITERIA.replace(
          '<QUALIFICATIONS_NAME>',
          criterias
        )
      );

      const isRangeInvalid = Common.isInvalidRangeForFeasibility(
        Array.from(quotaSet.values())
      );

      if (!isRangeInvalid) {
        this._supportService
          .notifyNoFeasibility(
            request?.project_id ?? 0,
            this._auth.user?.eml ?? '',
            criterias,
            qualificationCodes,
            request?.survey_localization ?? ''
          )
          .subscribe();
      }

      // pushing into scope so that we do not show the warning message again on feasibility load.
      this.unusedQualificationsNameList.push(...names);
    }
    this.totalFeasibility = response.total_feasibility;
    this.feasibilityQuota = mappedQuotas;
  }

  private _isNested(criteriaQuotas: any[]) {
    return criteriaQuotas.some(
      (quota) => quota.quota_category === 'autoNested'
    );
  }

  private _getCriteriaNames(
    quota: CriteriaQuota & { isNested?: boolean }
  ): string {
    let criteriaLabel: string[] = [];
    for (const criteria of quota.criteria) {
      if (criteria.condition_codes) {
        const condition = this._filterConditionNames(
          criteria.qualification_code,
          criteria.condition_codes,
          criteria.group_name
        );
        criteriaLabel = criteriaLabel.concat(condition);
      }
      if (criteria.range_sets) {
        const condition = this._getRangeNames(
          criteria.qualification_code,
          criteria.range_sets
        );
        criteriaLabel = criteriaLabel.concat(condition);
      }
    }
    return this._buildFinalConditionLabel(criteriaLabel);
  }

  private _buildFinalConditionLabel(criteriaLabel: Array<string>): string {
    let finalLabel = criteriaLabel.filter(Boolean).toString();
    if (finalLabel.length > Constants.SUBSTRING_VALUE) {
      finalLabel = finalLabel
        .substring(0, Constants.SUBSTRING_VALUE)
        .concat('...');
    }
    return finalLabel;
  }

  setFeasibilityStatus() {
    if (this.loading) {
      // loading
      this.feasibilityStatus = 'warning';
      return;
    }
    if (!this.feasibilityQuota.length) {
      this.feasibilityStatus = 'none';
      return;
    }
    const isNotFeasible = this.feasibilityQuota.some((f) =>
      f.quotas.some((q) => !q.feasible)
    );
    this.feasibilityStatus = isNotFeasible ? 'danger' : 'success';
  }

  private _filterConditionNames(
    qualificationCode: number,
    codes: string[],
    groupName?: string
  ) {
    if (FILE_UPLOAD_LOCATION_QUOTAS.includes(qualificationCode)) {
      return groupName ? groupName : '';
    }
    return this._getConditionNames(qualificationCode, codes);
  }

  private _getConditionNames(qualificationCode: number, codes: string[]) {
    let conditionNames: string[] | undefined = [];
    codes.forEach((code) => {
      this._qualifications
        .find(
          (qualification) =>
            qualification.qualification_code === qualificationCode
        )
        ?.condition_names?.filter((names) =>
          Object.keys(names).includes(code.toString())
        )
        // @ts-ignore
        .map((codeNames) => conditionNames.push(codeNames[code.toString()]));
    });

    return conditionNames.length > 1
      ? conditionNames.join(',').substring(0, 20).concat('...')
      : conditionNames[0];
  }

  private _getRangeNames(qualificationCode: number, rangeSet: RangeSet[]) {
    let rangeNames: string[] = [];
    const qualificationFormatter = this._formatters.get(qualificationCode)!;
    if (qualificationFormatter) {
      rangeSet.forEach((range) => {
        rangeNames.push(qualificationFormatter.formatRange(range));
      });
    }
    return rangeNames.join(',');
  }

  private _makeQualificationFormatters() {
    const qualificationFormatters = new Map<number, QualificationFormatter>();
    const { prefix: currencySymbol } = this.DEFAULT_INCOME_VALUES;
    qualificationFormatters.set(
      QUALIFICATION_CODES.AGE,
      new QualificationFormatter(
        QUALIFICATION_CODES.AGE,
        'Age',
        new StringFormatter(undefined, ' yr - ', ' yr')
      )
    );
    qualificationFormatters.set(
      QUALIFICATION_CODES.INCOME,
      new QualificationFormatter(
        QUALIFICATION_CODES.INCOME,
        'Income',
        new StringFormatter(currencySymbol, ` - ${currencySymbol}`)
      )
    );
    this._qualifications.forEach((q) => {
      const code: number = q.qualification_code;
      const name: string = q.qualification_name;
      const hasQualification = qualificationFormatters.has(code);
      if (!hasQualification) {
        qualificationFormatters.set(
          code,
          new QualificationFormatter(
            code,
            `${name[0]}${name.toLowerCase().substring(1)}`,
            new StringFormatter()
          )
        );
      }
    });
    return qualificationFormatters;
  }

  private _initializeUnicornQuotas() {
    this._quotaPayloadService
      .getSurveyUnicorns()
      .pipe(share())
      .subscribe((unicornQuotas: UnicornQuotas[]) => {
        this.unicornQuotas = unicornQuotas;
      });
  }

  public getQuotaRowClass(quota: FeasibilityQuota) {
    const isQuotaPresentInDifficultQuotas = new SurveyDifficultQuotas(
      this._difficultQuotas
    ).hasQuota(quota.buyer_quota_id!);
    const isQuotaPresentInUnicorn = new SurveyUnicorns(
      this.unicornQuotas
    ).hasQuota(quota.buyer_quota_id!);

    if (!quota.feasible || isQuotaPresentInUnicorn) {
      return {
        'unknown-tr': true,
      };
    }

    if (isQuotaPresentInDifficultQuotas) {
      return {
        'difficult-quotas-yellow': true,
      };
    }

    return {};
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }
}
